import {
    ReactElement,
    FormEventHandler,
    useCallback,
    useEffect,
    useState
} from 'react';
import { Trans } from 'react-i18next';
import {
    UICheckbox,
    UIExternalLinkTextButton,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISpacer
} from '@/ui';
import { useMeTranslation } from '../hooks';
import { SsoConsent } from '../actions';

export type SsoConsentModalProps = {
    isOpen: boolean;
    loading: boolean;
    onClose: () => void;
    onSubmit: (consent: SsoConsent) => void;
};

/**
 * Shown when a sign-in would create an account. The API asks for the same
 * agreements the password signup form collects, so it collects them the same
 * way: one legal checkbox covering all three, plus the marketing opt-in.
 */
export function SsoConsentModal(props: SsoConsentModalProps): ReactElement {
    const { isOpen, loading, onClose } = props;

    return (
        <UIModal isOpen={isOpen} onClose={loading ? () => undefined : onClose}>
            {isOpen ? <ConsentForm {...props} /> : <></>}
        </UIModal>
    );
}

function ConsentForm(props: SsoConsentModalProps): ReactElement {
    const { loading, onClose, onSubmit } = props;
    const { t } = useMeTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    const [legal, setLegal] = useState(false);
    const [legalError, setLegalError] = useState('');
    const [marketing, setMarketing] = useState(false);

    const checkLegal = useCallback(
        (legal: boolean): string =>
            legal ? '' : t('shared.legal.error_required'),
        [t]
    );

    useEffect(() => {
        if (showRealTimeErrors) {
            setLegalError(checkLegal(legal));
        }
    }, [legal, showRealTimeErrors, checkLegal]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (checkLegal(legal)) {
            return;
        }

        setShowRealTimeErrors(false);

        // The single legal checkbox stands for all three agreements, exactly as
        // it does on the password signup form.
        onSubmit({
            termsOfService: true,
            privacyPolicy: true,
            over16: true,
            contactOptIn: marketing
        });
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UIModalHeader
                title={t('components.sso_consent_modal.title')}
                subtitle={t('components.sso_consent_modal.description')}
            />
            <UIModalContent>
                <UICheckbox
                    name="legal"
                    checked={legal}
                    onChange={setLegal}
                    description={
                        <Trans
                            ns="me"
                            i18nKey="shared.legal.description"
                            components={{
                                a1: (
                                    <UIExternalLinkTextButton
                                        variant="primary"
                                        href={
                                            'https://www.safepointapp.com/end-user-license-agreement'
                                        }
                                    />
                                ),
                                a2: (
                                    <UIExternalLinkTextButton
                                        variant="primary"
                                        href={
                                            'https://www.safepointapp.com/privacy-policy'
                                        }
                                    />
                                )
                            }}
                        />
                    }
                    errorText={legalError}
                    required
                />

                <UISpacer multiplier={2} />

                <UICheckbox
                    name="marketing"
                    checked={marketing}
                    onChange={setMarketing}
                    description={t('shared.legal.marketing_opt_in')}
                />
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: t('shared.labels.continue')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </form>
    );
}
